import React, { useState, useEffect } from "react";
import {
    Button, Dialog, DialogActions, DialogContent, FormControl, MenuItem,
    TableRow, Table, TableHead, TableBody, TableCell, TableContainer, Paper,
    Card, Box, Grid, Select, TextField, Typography, IconButton, FormHelperText,
    Chip, InputAdornment, Tooltip, Menu, Checkbox, Avatar, FormControlLabel,
} from "@material-ui/core";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import TeacherService from "./Locality/Service/teacherService";
import { formatDate } from "../../utils/dateFormat";
import TeacherNotesService from "./Locality/Service/teacherNotesService";
import * as Yup from 'yup';
import TablePagination from '@material-ui/core/TablePagination';
import { useFormik } from 'formik';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import FilterListIcon from '@material-ui/icons/FilterList';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityIcon from '@material-ui/icons/Visibility';
import SchoolIcon from '@material-ui/icons/School';
import { makeStyles } from '@material-ui/core/styles';
import FilterPanel, { FilterSection } from '../../components/ui/FilterPanel';

const studentNameFromNote = (row) => {
    const s = row?.studentId;
    if (!s) return "—";
    if (typeof s === 'object') {
        return `${s.firstName || ""} ${s.lastName || ""}`.trim() || "—";
    }
    return "—";
};

/** Preview text only — never show raw Mongo/JSON ids in the delete dialog. */
const humanNotePreview = (notes) => {
    if (notes == null) return "";
    if (typeof notes === "object") {
        if (typeof notes.notes === "string") return humanNotePreview(notes.notes);
        return "";
    }
    const text = String(notes).trim();
    if (!text) return "";
    const looksLikeRawRecord =
        (text.startsWith("{") || text.startsWith("[")) &&
        (/["']_id["']/.test(text) || text.includes("isFuturePlanning") || text.includes("studentId"));
    if (looksLikeRawRecord) return "";
    return text;
};

const studentInitialsFromName = (name) => {
    if (!name || name === "—") return "?";
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

const formatNoteDate = (value) => formatDate(value) || "—";

const creatorLabel = (row) => {
    const user = row?.userId;
    const teacher = row?.teacherId;
    const name = user?.userName || user?.mobile || teacher?.teacherName || "—";
    // Stored role code is "SCHOOLE"; show the correct spelling in the UI only.
    const rawRole = user?.role || "";
    const role = String(rawRole).toUpperCase() === "SCHOOLE" ? "SCHOOL" : rawRole;
    return role ? `${name} (${role})` : name;
};

const groupNotesByStudent = (notes) => {
    const map = new Map();
    notes.forEach((note) => {
        const sid = String(note.studentId?._id || note.studentId || "unknown");
        if (!map.has(sid)) {
            map.set(sid, {
                studentId: sid,
                studentName: studentNameFromNote(note),
                notes: [],
            });
        }
        map.get(sid).notes.push(note);
    });
    return Array.from(map.values()).sort((a, b) =>
        a.studentName.localeCompare(b.studentName)
    );
};

const normalizeId = (value) => {
    if (!value) return "";
    if (typeof value === "object" && value._id) return String(value._id);
    return String(value);
};

const useStyles = makeStyles((theme) => ({
    pageHeader: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    pageTitle: {
        fontWeight: 700,
        fontSize: "1.9rem",
        color: "#1a1a1a",
        lineHeight: 1.2,
    },
    pageSubtitle: {
        color: "#555",
        fontSize: "0.9rem",
        marginTop: 2,
    },
    noteBanner: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        borderRadius: 12,
        backgroundColor: "#e6e6e6",
        border: "1px solid #d6e4ff",
        marginBottom: theme.spacing(2),
    },
    noteIconWrap: {
        width: 28,
        height: 28,
        borderRadius: "50%",
        backgroundColor: "#1a1a1a",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    filtersCard: {
        borderRadius: 14,
        border: "1px solid #d9d9d9",
        boxShadow: "none",
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    filtersRow: {
      display: 'flex',
      alignItems: 'flex-end',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      width: '100%',
    },
    filtersChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 40,
      boxSizing: 'border-box',
      padding: '0 12px',
      borderRadius: 8,
      border: '1px solid #8f8f8f',
      backgroundColor: '#fff',
      cursor: 'pointer',
      fontWeight: 600,
      color: '#1a1a1a',
      userSelect: 'none',
      flexShrink: 0,
    },
    countBadge: {
        backgroundColor: "#1a1a1a",
        color: "#fff",
        borderRadius: 10,
        fontSize: "0.72rem",
        fontWeight: 700,
        minWidth: 18,
        height: 18,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 5px",
    },
    resetBtn: {
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: 8,
      color: '#444',
      border: '1px solid #8f8f8f',
      height: 40,
      minWidth: 88,
      padding: '0 16px',
    },
    filterFieldWrap: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      minWidth: 150,
    },
    filterActionsWrap: {
      display: 'flex',
      flexDirection: 'column',
      flex: '0 0 auto',
      justifyContent: 'flex-end',
    },
    tableCard: {
        borderRadius: 14,
        border: "1px solid #d9d9d9",
        boxShadow: "none",
        overflow: "hidden",
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        borderBottom: "1px solid #d9d9d9",
    },
    iconBtnOutlined: {
        border: "1px solid #d9d9d9",
        borderRadius: 8,
        padding: 7,
    },
    headCell: {
        backgroundColor: "#efefef",
        color: "#444",
        fontWeight: 700,
        fontSize: "0.8rem",
        borderBottom: "1px solid #d9d9d9",
        whiteSpace: "nowrap",
    },
    classChip: {
        backgroundColor: "#e6e6e6",
        color: "#1a1a1a",
        fontWeight: 600,
        height: 24,
    },
    sectionChip: {
        backgroundColor: "#f3efff",
        color: "#7c4dff",
        fontWeight: 600,
        height: 24,
    },
    studentAvatar: {
        width: 28,
        height: 28,
        fontSize: "0.72rem",
        fontWeight: 700,
        backgroundColor: "#1a1a1a",
        color: "#fff",
    },
    addBtn: {
        backgroundColor: "#1a1a1a",
        color: "#fff",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 8,
        padding: "8px 18px",
        boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
        "&:hover": { backgroundColor: "#125bbd" },
    },
    notesCell: {
        color: "#444",
    },
    notesViewBtn: {
        padding: 6,
        color: "#1a1a1a",
    },
    viewNoteText: {
        color: "#1a1a1a",
        fontSize: "0.95rem",
        lineHeight: 1.6,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    viewMetaRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 16,
    },
    dialogPaper: {
        borderRadius: 14,
        width: 480,
        maxWidth: '95vw',
    },
    dialogTitleBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 2.5),
        borderBottom: '1px solid #d9d9d9',
    },
    dialogTitleText: {
        fontWeight: 700,
        fontSize: '1.15rem',
        color: '#1a1a1a',
    },
    dialogBody: {
        padding: theme.spacing(2.5),
    },
    fieldLabel: {
        display: 'block',
        fontSize: '0.78rem',
        fontWeight: 600,
        color: '#444',
        marginBottom: 6,
    },
    dialogFooter: {
        padding: theme.spacing(1.5, 2.5),
        borderTop: '1px solid #d9d9d9',
    },
    cancelBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        color: '#444',
    },
    saveBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        backgroundColor: '#1a1a1a',
        color: '#fff',
        boxShadow: '0 4px 10px rgba(23,105,214,0.25)',
        '&:hover': { backgroundColor: '#125bbd' },
    },
    deleteBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        backgroundColor: '#b3261e',
        color: '#fff',
        '&:hover': { backgroundColor: '#b71c1c' },
    },
    deleteConfirmText: {
        color: '#444',
        fontSize: '0.9rem',
        lineHeight: 1.6,
    },
    groupHeaderRow: {
        backgroundColor: '#e6e6e6',
    },
    groupHeaderCell: {
        padding: '10px 16px !important',
        cursor: 'pointer',
        userSelect: 'none',
    },
    groupHeaderInner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    activityCountBadge: {
        backgroundColor: '#fff',
        color: '#1a1a1a',
        border: '1px solid #c4c4c4',
        borderRadius: 12,
        padding: '1px 10px',
        fontSize: '0.72rem',
        fontWeight: 700,
        marginLeft: 8,
    },
    noteDetailMeta: {
        fontSize: '0.75rem',
        color: '#8a8a8a',
        marginTop: 4,
    },
    selectAllWrap: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 4,
        padding: '4px 0',
        borderBottom: '1px solid #d9d9d9',
    },
}));
export default function TeacherNotes() {
    const classes = useStyles();
    const [studentList, setStudentList] = useState([]);
    const [addClassList, setAddClassList] = useState([]);
    const [teacherNotesList, setTeacherNotesList] = useState([]);
    const [open, setOpen] = useState(false);
    const [pg, setpg] = useState(0);
    const [rpg, setrpg] = useState(10);
    const [classId, setClassId] = useState("");
    const [sectionList, setSectionList] = useState([]);
    const [filterClassId, setFilterClassId] = useState("");
    const [filterSectionId, setFilterSectionId] = useState("");
    const [filterSectionList, setFilterSectionList] = useState([]);
    const [teacherHasMultipleClasses, setTeacherHasMultipleClasses] = useState(false);
    const [teacherCanPickSection, setTeacherCanPickSection] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [actionMenu, setActionMenu] = useState({ anchor: null, row: null });
    const [viewOpen, setViewOpen] = useState(false);
    const [viewRow, setViewRow] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, row: null });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [studentRowsExpanded, setStudentRowsExpanded] = useState({});
    const [visibleCols, setVisibleCols] = useState({
        notes: true,
        class: true,
        section: true,
        createdAt: true,
        creator: true,
    });
    const [teacherNotes, setTeacherNotes] = useState({
        classId: '',
        sectionId: '',
        notes: '',
        studentId: '',
        studentIds: [],
    });

    const columnConfig = [
        { id: "notes", label: "Notes" },
        { id: "class", label: "Class" },
        { id: "section", label: "Section" },
        { id: "createdAt", label: "Created" },
        { id: "creator", label: "Created By" },
    ];

    const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
    const isTeacher = userDetails.role === "TEACHER";
    const isEditMode = Boolean(teacherNotes._id);

    const openActionMenu = (e, row) => setActionMenu({ anchor: e.currentTarget, row });
    const closeActionMenu = () => setActionMenu({ anchor: null, row: null });

    const openViewNote = (row) => {
        setViewRow(row || null);
        setViewOpen(true);
    };

    const closeViewNote = () => {
        setViewOpen(false);
        setViewRow(null);
    };

    const getStudentGroupKey = (group) => String(group.studentId);

    const isStudentGroupExpanded = (key) => studentRowsExpanded[key] === true;

    const toggleStudentGroup = (key) => {
        setStudentRowsExpanded((prev) => ({
            ...prev,
            [key]: !(prev[key] === true),
        }));
    };

    const buildNotesListPayload = () => {
        const payload = { schooleId: userDetails.schooleId };
        if (isTeacher && userDetails.teacherId) {
            payload.teacherId = userDetails.teacherId;
        }
        if (filterClassId) payload.classId = filterClassId;
        if (filterSectionId) payload.sectionId = filterSectionId;
        return payload;
    };

    const activeFilterCount =
        (filterClassId ? 1 : 0) +
        (filterSectionId ? 1 : 0);

    const handleResetFilters = () => {
        setpg(0);
        if (isTeacher) {
            syncTeacherProfile();
            return;
        }
        setFilterClassId("");
        setFilterSectionId("");
        setFilterSectionList([]);
    };

    const filteredList = teacherNotesList.filter((row) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        return (
            studentNameFromNote(row).toLowerCase().includes(term) ||
            (row.classId?.className || "").toLowerCase().includes(term) ||
            (row.sectionId?.sectionName || "").toLowerCase().includes(term) ||
            (row.notes || "").toLowerCase().includes(term) ||
            creatorLabel(row).toLowerCase().includes(term) ||
            (row.teacherId?.teacherName || "").toLowerCase().includes(term) ||
            (row.schooleId?.schooleName || "").toLowerCase().includes(term)
        );
    });

    const groupedStudents = groupNotesByStudent(filteredList);
    const visibleColCount = columnConfig.filter((c) => visibleCols[c.id]).length + 1;

    const validationSchema = Yup.object().shape({
        classId: Yup.string().required('Class Name is required'),
        notes: Yup.string().required('notes is required'),
        studentId: Yup.string(),
        studentIds: Yup.array(),
    }).test('student-selection', 'At least one student is required', function validateStudents(values) {
        if (teacherNotes._id) {
            return Boolean(values.studentId);
        }
        return Array.isArray(values.studentIds) && values.studentIds.length > 0;
    });

    const handleChangePage = (event, newpage) => {
        setpg(newpage);
    };
    const handleChangeRowsPerPage = (event) => {
        setrpg(parseInt(event.target.value, 10));
        setpg(0);
    };

    const getTeacherNotesList = () => {
        TeacherNotesService.getAllTeacherNotes(buildNotesListPayload()).then((res) => {
            setTeacherNotesList(Array.isArray(res) ? res : []);
        }).catch((err) => {
            console.error(err.message);
        });
    };

    useEffect(() => {
        getAddClassList();
        return () => {
            setStudentList([]);
            setAddClassList([]);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getTeacherNotesList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterClassId, filterSectionId]);

    const getFilterSectionListByClass = (selectedClassId) => {
        if (!selectedClassId) {
            setFilterSectionList([]);
            if (isTeacher) setTeacherCanPickSection(false);
            return Promise.resolve([]);
        }

        const body = {
            schooleId: userDetails.schooleId,
            classId: selectedClassId,
        };
        if (isTeacher && userDetails.teacherId) {
            body.teacherId = userDetails.teacherId;
        }

        return SectionService.findSectionList(body)
            .then((res) => {
                const list = Array.isArray(res) ? res : [];
                setFilterSectionList(list);
                if (isTeacher) {
                    setTeacherCanPickSection(list.length > 1);
                }
                return list;
            })
            .catch(() => {
                setFilterSectionList([]);
                return [];
            });
    };

    const applyFilterClassSection = (selectedClassId, preferredSectionId) => {
        const cid = normalizeId(selectedClassId);
        if (!cid) return Promise.resolve();
        setFilterClassId(cid);
        setClassId(cid);
        return getFilterSectionListByClass(cid).then((rows) => {
            let validSection = "";
            if (rows.length === 1) {
                validSection = normalizeId(rows[0]._id);
            } else if (
                preferredSectionId &&
                rows.some((s) => normalizeId(s._id) === normalizeId(preferredSectionId))
            ) {
                validSection = normalizeId(preferredSectionId);
            }
            setFilterSectionId(validSection);
            setpg(0);
        });
    };

    const syncTeacherProfile = () => {
        const storedClassId = normalizeId(userDetails.classId);
        const storedSectionId = normalizeId(userDetails.sectionId);

        if (!userDetails.teacherId) {
            if (storedClassId) return applyFilterClassSection(storedClassId, storedSectionId);
            return Promise.resolve();
        }

        return TeacherService.getTeacherById(userDetails.teacherId)
            .then((teacher) => {
                const classIds = new Set();
                const primaryClassId = normalizeId(teacher?.classId?._id || teacher?.classId);
                if (primaryClassId) classIds.add(primaryClassId);
                (teacher?.additionalClassAndSection || []).forEach((item) => {
                    const cid = normalizeId(item?.class?.classId?._id || item?.class?.classId);
                    if (cid) classIds.add(cid);
                });
                setTeacherHasMultipleClasses(classIds.size > 1);

                const freshClassId = primaryClassId || storedClassId;
                const freshSectionId =
                    normalizeId(teacher?.sectionId?._id || teacher?.sectionId) || storedSectionId;
                return applyFilterClassSection(freshClassId, freshSectionId);
            })
            .catch(() => {
                setTeacherHasMultipleClasses(false);
                if (storedClassId) return applyFilterClassSection(storedClassId, storedSectionId);
                return Promise.resolve();
            });
    };

    const handleOpen = (isAdd = false) => {
        if (isAdd) {
            const reset = {
                classId: '',
                sectionId: '',
                notes: '',
                studentId: '',
                studentIds: [],
            };
            if (isTeacher && filterClassId) {
                reset.classId = filterClassId;
                reset.sectionId = filterSectionId || '';
            } else if (isTeacher && (userDetails.classId || userDetails.sectionId)) {
                reset.classId = userDetails.classId || '';
                reset.sectionId = userDetails.sectionId || '';
            }
            setTeacherNotes(reset);
            if (reset.classId) {
                getSectionListByClass(reset.classId);
                loadStudentsForNotes(reset.classId, reset.sectionId || undefined);
            } else {
                setStudentList([]);
            }
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const onFilterClassChange = (e) => {
        const id = e.target.value;
        setFilterClassId(id);
        setClassId(id);
        setFilterSectionId("");
        setpg(0);
        if (id) {
            getFilterSectionListByClass(id);
        } else {
            setFilterSectionList([]);
            if (isTeacher) setTeacherCanPickSection(false);
        }
    };

    const onFilterSectionChange = (e) => {
        setFilterSectionId(e.target.value);
        setpg(0);
    };

    const getSectionListByClass = (selectedClassId) => {
        if (!selectedClassId) {
            setSectionList([]);
            return;
        }
        const body = {
            schooleId: userDetails.schooleId,
            classId: selectedClassId,
        };
        if (isTeacher && userDetails.teacherId) {
            body.teacherId = userDetails.teacherId;
        }
        SectionService.findSectionList(body).then((res) => {
            setSectionList(Array.isArray(res) ? res : []);
        }).catch(() => setSectionList([]));
    };

    const getAddClassList = () => {
        const schooleId = userDetails.schooleId?._id || userDetails.schooleId;
        AddClassService.getAllAddClass(schooleId, isTeacher ? 'TEACHER' : undefined, true)
            .then((res) => {
                const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
                setAddClassList(list);
                if (isTeacher) {
                    return syncTeacherProfile();
                }
                setClassId("");
                return Promise.resolve();
            })
            .catch(() => {
                setAddClassList([]);
            });
    };

    const editTeacherNotes = (teachernotes) => {
        const classId = normalizeId(teachernotes.classId);
        const sectionId = normalizeId(teachernotes.sectionId);
        const studentId = normalizeId(teachernotes.studentId);
        const studentName = studentNameFromNote(teachernotes);
        const obj = {
            ...teachernotes,
            classId,
            sectionId,
            studentId,
            studentIds: [],
        };
        setTeacherNotes(obj);
        // Show the note's student immediately so the Select is not blank while the list loads.
        if (studentId) {
            setStudentList([{
                _id: studentId,
                studentName: studentName !== "—" ? studentName : "Selected student",
            }]);
        }
        getSectionListByClass(classId);
        loadStudentsForNotes(classId, sectionId || undefined, {
            studentId,
            studentName: studentName !== "—" ? studentName : "Selected student",
        });
        handleOpen(false);
    };

    const openDeleteConfirm = (row) => {
        setDeleteConfirm({ open: true, row: row || null });
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirm({ open: false, row: null });
        setDeleteLoading(false);
    };

    const confirmDeleteTeacherNotes = () => {
        const notesdelete = deleteConfirm.row;
        if (!notesdelete?._id) return;

        setDeleteLoading(true);
        TeacherNotesService.deleteTeacherNotes(notesdelete)
            .then(() => {
                closeDeleteConfirm();
                getTeacherNotesList();
            })
            .catch(() => {})
            .finally(() => {
                setDeleteLoading(false);
            });
    };

    const loadStudentsForNotes = (selectedClassId, sectionIdValue, ensureStudent) => {
        if (!selectedClassId) {
            setStudentList([]);
            return Promise.resolve([]);
        }

        const payload = {
            schooleId: userDetails.schooleId?._id || userDetails.schooleId,
            classId: selectedClassId,
        };
        if (sectionIdValue) payload.sectionId = sectionIdValue;

        return TeacherNotesService.getStudentsForNotes(payload)
            .then((res) => {
                let list = Array.isArray(res) ? res : [];
                list = list.map((s) => ({
                    ...s,
                    _id: normalizeId(s._id),
                    studentName:
                        s.studentName ||
                        `${s.firstName || ""} ${s.lastName || ""}`.trim(),
                }));
                const ensureId = normalizeId(ensureStudent?.studentId);
                if (ensureId && !list.some((s) => normalizeId(s._id) === ensureId)) {
                    list = [
                        {
                            _id: ensureId,
                            studentName: ensureStudent.studentName || "Selected student",
                        },
                        ...list,
                    ];
                }
                setStudentList(list);
                return list;
            })
            .catch((err) => {
                console.error("Error fetching students:", err.message);
                const ensureId = normalizeId(ensureStudent?.studentId);
                if (ensureId) {
                    setStudentList([{
                        _id: ensureId,
                        studentName: ensureStudent.studentName || "Selected student",
                    }]);
                } else {
                    setStudentList([]);
                }
                return [];
            });
    };

    const formik = useFormik({
        initialValues: teacherNotes,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            const currentUser = JSON.parse(localStorage.getItem("userDetail"));
            const payload = {
                classId: values.classId,
                sectionId: values.sectionId || undefined,
                notes: values.notes,
                schooleId: currentUser.schooleId,
                teacherId: currentUser.teacherId || currentUser._id,
                userId: currentUser._id,
            };

            if (teacherNotes._id) {
                payload._id = teacherNotes._id;
                payload.studentId = values.studentId;
                TeacherNotesService.upadeTeacherNotes(payload)
                    .then(() => {
                        handleClose();
                        getTeacherNotesList();
                        resetForm();
                        alert("Updated Successfully.");
                    })
                    .catch((err) => {
                        alert(err.response?.data?.message || "Update failed");
                    });
            } else {
                payload.studentIds = values.studentIds || [];
                TeacherNotesService.creteTeacherNotes(payload)
                    .then((res) => {
                        getTeacherNotesList();
                        resetForm();
                        handleClose();
                        const count = res?.insertedCount || payload.studentIds.length;
                        alert(`Added Successfully for ${count} student${count === 1 ? '' : 's'}.`);
                    })
                    .catch((err) => {
                        alert(err.response?.data?.message || "Creation failed");
                    });
            }
        },
    });

    const allStudentsSelected =
        studentList.length > 0 &&
        (formik.values.studentIds || []).length === studentList.length;

    const someStudentsSelected =
        (formik.values.studentIds || []).length > 0 && !allStudentsSelected;

    const handleSelectAllStudents = (checked) => {
        if (checked) {
            formik.setFieldValue('studentIds', studentList.map((s) => s._id));
        } else {
            formik.setFieldValue('studentIds', []);
        }
    };

    const deleteNotePreview = humanNotePreview(deleteConfirm.row?.notes);

    return (
        <Box p={{ xs: 1, sm: 2 }}>
            <Box className={classes.pageHeader}>
                <Box>
                    <Typography className={classes.pageTitle}>Notes For Report</Typography>
                    <Typography className={classes.pageSubtitle}>
                        Add and manage teacher notes for student reports by class and section.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    className={classes.addBtn}
                    startIcon={<AddIcon />}
                    onClick={() => handleOpen(true)}
                >
                    Add Notes
                </Button>
            </Box>


            <Card elevation={0} className={classes.tableCard}>
                <Box className={classes.toolbar}>
                    <Typography style={{ fontWeight: 700, color: "#1a1a1a" }}>
                        {groupedStudents.length} Student{groupedStudents.length !== 1 ? "s" : ""} · {filteredList.length} Note{filteredList.length !== 1 ? "s" : ""}
                    </Typography>
                    <Box display="flex" alignItems="center" gridGap={10}>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search notes..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setpg(0);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ fontSize: 18, color: "#8a8a8a" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Tooltip title="Columns">
                            <IconButton className={classes.iconBtnOutlined} size="small" onClick={(e) => setColumnMenuAnchor(e.currentTarget)}>
                                <ViewColumnIcon style={{ fontSize: 20, color: "#444" }} />
                            </IconButton>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          startIcon={<FilterListIcon fontSize="small" />}
                          onClick={() => setFiltersOpen((o) => !o)}
                          aria-pressed={filtersOpen}
                          style={filtersOpen ? { backgroundColor: '#e6e6e6' } : undefined}
                        >
                          Filters
                          {activeFilterCount > 0 && <span className={classes.activityCountBadge}>{activeFilterCount}</span>}
                        </Button>
                        <Tooltip title="Refresh">
                            <IconButton className={classes.iconBtnOutlined} size="small" onClick={getTeacherNotesList}>
                                <RefreshIcon style={{ fontSize: 20, color: "#444" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box display="flex" alignItems="stretch" flexWrap="wrap">
                <Box flex="1 1 520px" minWidth={0}>
                <TableContainer component={Paper} elevation={0} style={{ maxHeight: 520 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {columnConfig.filter((c) => visibleCols[c.id]).map((c) => (
                                    <TableCell key={c.id} className={classes.headCell}>{c.label}</TableCell>
                                ))}
                                <TableCell className={classes.headCell} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupedStudents.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={visibleColCount} align="center" style={{ padding: 32, color: "#8a8a8a" }}>
                                        No notes found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                groupedStudents.slice(pg * rpg, pg * rpg + rpg).map((group) => {
                                    const groupKey = getStudentGroupKey(group);
                                    const expanded = isStudentGroupExpanded(groupKey);
                                    return (
                                        <React.Fragment key={groupKey}>
                                            <TableRow className={classes.groupHeaderRow}>
                                                <TableCell
                                                    colSpan={visibleColCount}
                                                    className={classes.groupHeaderCell}
                                                    onClick={() => toggleStudentGroup(groupKey)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            toggleStudentGroup(groupKey);
                                                        }
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-expanded={expanded}
                                                >
                                                    <Box className={classes.groupHeaderInner}>
                                                        <Box display="flex" alignItems="center" gridGap={10}>
                                                            <Avatar className={classes.studentAvatar}>
                                                                {studentInitialsFromName(group.studentName)}
                                                            </Avatar>
                                                            <Typography style={{ fontWeight: 700, color: '#1a1a1a', textTransform: 'uppercase', fontSize: '0.9rem' }}>
                                                                {group.studentName}
                                                            </Typography>
                                                            <span className={classes.activityCountBadge}>
                                                                {group.notes.length} {group.notes.length === 1 ? 'Note' : 'Notes'}
                                                            </span>
                                                        </Box>
                                                        {expanded ? (
                                                            <ExpandMoreIcon style={{ color: '#444' }} />
                                                        ) : (
                                                            <ChevronRightIcon style={{ color: '#444' }} />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                            {expanded
                                                ? group.notes.map((row) => (
                                                    <TableRow key={row._id} hover>
                                                        {visibleCols.notes && (
                                                            <TableCell className={classes.notesCell}>
                                                                {row.notes ? (
                                                                    <Tooltip title="View note">
                                                                        <IconButton
                                                                            size="small"
                                                                            className={classes.notesViewBtn}
                                                                            onClick={() => openViewNote(row)}
                                                                            aria-label="View note"
                                                                        >
                                                                            <VisibilityIcon style={{ fontSize: 18 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Typography style={{ color: "#8a8a8a", fontSize: "0.875rem" }}>
                                                                        —
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                        )}
                                                        {visibleCols.class && (
                                                            <TableCell>
                                                                <Chip size="small" label={row.classId?.className || "—"} className={classes.classChip} />
                                                            </TableCell>
                                                        )}
                                                        {visibleCols.section && (
                                                            <TableCell>
                                                                <Chip size="small" label={row.sectionId?.sectionName || "—"} className={classes.sectionChip} />
                                                            </TableCell>
                                                        )}
                                                        {visibleCols.createdAt && (
                                                            <TableCell style={{ color: '#444', fontSize: '0.875rem' }}>
                                                                {formatNoteDate(row.createdAt)}
                                                            </TableCell>
                                                        )}
                                                        {visibleCols.creator && (
                                                            <TableCell style={{ color: '#444', fontSize: '0.875rem' }}>
                                                                {creatorLabel(row)}
                                                            </TableCell>
                                                        )}
                                                        <TableCell align="right">
                                                            <IconButton size="small" onClick={(e) => openActionMenu(e, row)}>
                                                                <MoreVertIcon style={{ fontSize: 20, color: "#444" }} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                                : null}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={groupedStudents.length}
                    page={pg}
                    onPageChange={handleChangePage}
                    rowsPerPage={rpg}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
                </Box>
                {filtersOpen && (
                    <FilterPanel onReset={handleResetFilters}>
                    <FilterSection title="Class" icon={<SchoolIcon style={{ fontSize: 16 }} />}>
                            <FormControl variant="outlined" size="small" fullWidth>
                                <Select
                                    displayEmpty
                                    value={filterClassId || ""}
                                    onChange={onFilterClassChange}
                                    disabled={isTeacher && !teacherHasMultipleClasses}
                                >
                                    {!isTeacher && <MenuItem value=""><em>All Classes</em></MenuItem>}
                                    {addClassList.map(({ _id, className }) => (
                                        <MenuItem key={_id} value={_id}>{className}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                    </FilterSection>
                    <FilterSection title="Section" icon={<SchoolIcon style={{ fontSize: 16 }} />}>
                            <FormControl variant="outlined" size="small" fullWidth disabled={!filterClassId}>
                                <Select
                                    displayEmpty
                                    value={filterSectionId || ""}
                                    onChange={onFilterSectionChange}
                                    disabled={
                                        !filterClassId ||
                                        filterSectionList.length === 0 ||
                                        (isTeacher && !teacherCanPickSection)
                                    }
                                >
                                    <MenuItem value=""><em>All Sections</em></MenuItem>
                                    {filterSectionList.map(({ _id, sectionName }) => (
                                        <MenuItem key={_id} value={_id}>{sectionName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                    </FilterSection>
                    </FilterPanel>
                )}
                </Box>
            </Card>

            <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={() => setColumnMenuAnchor(null)}
            >
                {columnConfig.map((c) => (
                    <MenuItem key={c.id} onClick={() => setVisibleCols((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}>
                        <Checkbox checked={visibleCols[c.id]} size="small" color="primary" />
                        {c.label}
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                anchorEl={actionMenu.anchor}
                open={Boolean(actionMenu.anchor)}
                onClose={closeActionMenu}
            >
                <MenuItem
                    onClick={() => {
                        editTeacherNotes(actionMenu.row);
                        closeActionMenu();
                    }}
                >
                    <EditIcon style={{ fontSize: 18, marginRight: 8, color: "#444" }} />
                    Edit
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        openDeleteConfirm(actionMenu.row);
                        closeActionMenu();
                    }}
                >
                    <DeleteIcon style={{ fontSize: 18, marginRight: 8, color: "#b3261e" }} />
                    Delete
                </MenuItem>
            </Menu>
            <Dialog open={open} onClose={handleClose} scroll="paper" PaperProps={{ className: classes.dialogPaper }}>
                <Box className={classes.dialogTitleBar}>
                    <span className={classes.dialogTitleText}>
                        {teacherNotes._id ? 'Edit Notes' : 'Add Notes'}
                    </span>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon style={{ fontSize: 20, color: '#8a8a8a' }} />
                    </IconButton>
                </Box>
                <DialogContent className={classes.dialogBody}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <span className={classes.fieldLabel}>Class</span>
                            <FormControl
                                variant="outlined" size="small" fullWidth
                                error={formik.touched.classId && Boolean(formik.errors.classId)}
                            >
                                <Select
                                    id="demo-simple-select"
                                    name="classId"
                                    displayEmpty
                                    value={formik.values.classId}
                                    onChange={e => {
                                        formik.setFieldValue('sectionId', '');
                                        formik.setFieldValue('studentIds', []);
                                        formik.setFieldValue('studentId', '');
                                        formik.handleChange(e);
                                        getSectionListByClass(e.target.value);
                                        loadStudentsForNotes(e.target.value, undefined);
                                    }}
                                >
                                    <MenuItem value=""><em>Select Class</em></MenuItem>
                                    {addClassList.map(({ _id, className }) => (
                                        <MenuItem key={_id} value={_id}>{className}</MenuItem>
                                    ))}
                                </Select>
                                {formik.touched.classId && formik.errors.classId && (
                                    <FormHelperText>{formik.errors.classId}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <span className={classes.fieldLabel}>Section (Optional)</span>
                            <FormControl
                                variant="outlined" size="small" fullWidth
                                disabled={!formik.values.classId || sectionList.length === 0}
                            >
                                <Select
                                    id="sectionId"
                                    name="sectionId"
                                    displayEmpty
                                    value={formik.values.sectionId || ''}
                                    onChange={(e) => {
                                        formik.handleChange(e);
                                        formik.setFieldValue('studentIds', []);
                                        if (formik.values.classId) {
                                            loadStudentsForNotes(
                                                formik.values.classId,
                                                e.target.value || undefined
                                            );
                                        }
                                    }}
                                >
                                    <MenuItem value=""><em>All Sections</em></MenuItem>
                                    {sectionList.map(({ _id, sectionName }) => (
                                        <MenuItem key={_id} value={_id}>{sectionName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <span className={classes.fieldLabel}>
                                {isEditMode ? 'Student Name' : 'Students'}
                            </span>
                            {isEditMode ? (
                                <FormControl
                                    variant="outlined" size="small" fullWidth
                                    error={formik.touched.studentId && Boolean(formik.errors.studentId || formik.errors['student-selection'])}
                                >
                                    <Select
                                        id="studentName"
                                        name="studentId"
                                        displayEmpty
                                        value={normalizeId(formik.values.studentId) || ''}
                                        onChange={formik.handleChange}
                                    >
                                        <MenuItem value=""><em>Select Student</em></MenuItem>
                                        {studentList.map((student) => {
                                            const id = normalizeId(student._id);
                                            const label =
                                                student.studentName ||
                                                `${student.firstName || ""} ${student.lastName || ""}`.trim() ||
                                                "Student";
                                            return (
                                                <MenuItem key={id} value={id}>{label}</MenuItem>
                                            );
                                        })}
                                    </Select>
                                    {(formik.touched.studentId && formik.errors.studentId) && (
                                        <FormHelperText>{formik.errors.studentId}</FormHelperText>
                                    )}
                                </FormControl>
                            ) : (
                                <>
                                    <Box className={classes.selectAllWrap}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    size="small"
                                                    checked={allStudentsSelected}
                                                    indeterminate={someStudentsSelected}
                                                    onChange={(e) => handleSelectAllStudents(e.target.checked)}
                                                    disabled={!formik.values.classId || studentList.length === 0}
                                                />
                                            }
                                            label={
                                                <Typography variant="body2" style={{ color: '#444', fontWeight: 600 }}>
                                                    Select All Students
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                    <FormControl
                                        variant="outlined" size="small" fullWidth
                                        error={Boolean(formik.errors['student-selection'])}
                                    >
                                        <Select
                                            id="studentIds"
                                            name="studentIds"
                                            multiple
                                            displayEmpty
                                            disabled={!formik.values.classId}
                                            value={formik.values.studentIds || []}
                                            onChange={formik.handleChange}
                                            renderValue={(selected) => {
                                                if (!formik.values.classId) {
                                                    return <span style={{ color: '#8a8a8a' }}>Please select a class first</span>;
                                                }
                                                if (!selected.length) {
                                                    return <span style={{ color: '#8a8a8a' }}>Select students</span>;
                                                }
                                                const names = studentList
                                                    .filter(({ _id }) => selected.includes(_id))
                                                    .map(({ studentName }) => studentName);
                                                return names.length > 2
                                                    ? `${names.length} students selected`
                                                    : names.join(', ');
                                            }}
                                        >
                                            {studentList.length === 0 ? (
                                                <MenuItem disabled value="">
                                                    {formik.values.classId ? 'No students found' : 'Please select a class first'}
                                                </MenuItem>
                                            ) : (
                                                studentList.map(({ _id, studentName }) => (
                                                    <MenuItem key={_id} value={_id}>
                                                        <Checkbox
                                                            color="primary"
                                                            size="small"
                                                            checked={(formik.values.studentIds || []).indexOf(_id) > -1}
                                                        />
                                                        {studentName}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                        {formik.errors['student-selection'] && (
                                            <FormHelperText>{formik.errors['student-selection']}</FormHelperText>
                                        )}
                                    </FormControl>
                                </>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <span className={classes.fieldLabel}>Notes</span>
                            <TextField
                                fullWidth size="small" variant="outlined"
                                id="notes"
                                name="notes"
                                multiline
                                minRows={4}
                                maxRows={10}
                                placeholder="Write notes here..."
                                value={formik.values.notes}
                                onChange={formik.handleChange}
                                error={formik.touched.notes && Boolean(formik.errors.notes)}
                                helperText={formik.touched.notes && formik.errors.notes}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className={classes.dialogFooter}>
                    <Button onClick={handleClose} className={classes.cancelBtn}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        className={classes.saveBtn}
                        onClick={formik.handleSubmit}
                    >
                        {teacherNotes._id ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteConfirm.open}
                onClose={closeDeleteConfirm}
                scroll="paper"
                PaperProps={{ className: classes.dialogPaper }}
            >
                <Box className={classes.dialogTitleBar}>
                    <span className={classes.dialogTitleText}>Delete note?</span>
                    <IconButton size="small" onClick={closeDeleteConfirm} disabled={deleteLoading}>
                        <CloseIcon style={{ fontSize: 20, color: '#8a8a8a' }} />
                    </IconButton>
                </Box>
                <DialogContent className={classes.dialogBody}>
                    <Typography className={classes.deleteConfirmText}>
                        Are you sure you want to delete this teacher note for{" "}
                        <strong>{studentNameFromNote(deleteConfirm.row || {})}</strong>?
                    </Typography>
                    {deleteNotePreview ? (
                        <Typography
                            className={classes.deleteConfirmText}
                            style={{ marginTop: 12, fontStyle: 'italic' }}
                        >
                            &ldquo;{deleteNotePreview.slice(0, 120)}
                            {deleteNotePreview.length > 120 ? '…' : ''}&rdquo;
                        </Typography>
                    ) : null}
                    <Typography className={classes.deleteConfirmText} style={{ marginTop: 12, color: '#b3261e' }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions className={classes.dialogFooter}>
                    <Button onClick={closeDeleteConfirm} className={classes.cancelBtn} disabled={deleteLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.deleteBtn}
                        onClick={confirmDeleteTeacherNotes}
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={viewOpen}
                onClose={closeViewNote}
                scroll="paper"
                PaperProps={{ className: classes.dialogPaper }}
            >
                <Box className={classes.dialogTitleBar}>
                    <span className={classes.dialogTitleText}>View Note</span>
                    <IconButton size="small" onClick={closeViewNote}>
                        <CloseIcon style={{ fontSize: 20, color: '#8a8a8a' }} />
                    </IconButton>
                </Box>
                <DialogContent className={classes.dialogBody}>
                    <Box className={classes.viewMetaRow}>
                        <Chip
                            size="small"
                            avatar={
                                <Avatar className={classes.studentAvatar}>
                                    {studentInitialsFromName(studentNameFromNote(viewRow || {}))}
                                </Avatar>
                            }
                            label={studentNameFromNote(viewRow || {})}
                            style={{ fontWeight: 600 }}
                        />
                        {viewRow?.classId?.className && (
                            <Chip size="small" label={viewRow.classId.className} className={classes.classChip} />
                        )}
                        {viewRow?.sectionId?.sectionName && (
                            <Chip size="small" label={viewRow.sectionId.sectionName} className={classes.sectionChip} />
                        )}
                    </Box>
                    <Typography className={classes.noteDetailMeta} style={{ marginBottom: 12 }}>
                        {creatorLabel(viewRow || {})}
                        {viewRow?.createdAt ? ` · ${formatNoteDate(viewRow.createdAt)}` : ""}
                    </Typography>
                    <Typography className={classes.viewNoteText}>
                        {viewRow?.notes || "—"}
                    </Typography>
                </DialogContent>
                <DialogActions className={classes.dialogFooter}>
                    <Button onClick={closeViewNote} className={classes.cancelBtn}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
